import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n/I18nContext";
import { getSalesTrend } from "@/lib/api";
import { appPath } from "@/lib/paths";
import { ShoppingCart, ArrowRight, TrendingUp, TrendingDown, Minus } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const CHART_COLORS = ["#0E7490", "#14B8A6", "#F59E0B", "#3B82F6", "#22C55E", "#A855F7"];
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))" };
const BAR_CURSOR = { fill: "hsl(var(--secondary))" };
const CHART_MARGIN = { left: -14, right: 8, top: 8, bottom: 0 };
const LEGEND_STYLE = { fontSize: 12 };
const BAR_COLORS = ["hsl(var(--brand))", "hsl(var(--muted-foreground) / 0.45)"];

const PERIODS = ["7d", "30d", "90d", "month", "year"];
const ALL = "__all__";

export const SalesTrend = () => {
  const { t, td } = useT();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("30d");
  const [product, setProduct] = useState(ALL);
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getSalesTrend(period, product === ALL ? "" : product)
      .then((d) => {
        if (!alive) return;
        setData(d);
        // Keep the product list stable (endpoint returns all products regardless of filter).
        if (Array.isArray(d.products) && d.products.length) setProducts(d.products);
      })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period, product]);

  const periodLabel = {
    "7d": t("dashboard.period7d"), "30d": t("dashboard.period30d"), "90d": t("dashboard.period90d"),
    "month": t("dashboard.periodMonth"), "year": t("dashboard.periodYear"),
  };

  const currency = data?.currency || "RON";
  const revenue = data?.revenue || { current: 0, previous: 0, change_pct: null };
  const changePct = revenue.change_pct;
  const hasData = data && ((revenue.current || 0) > 0 || (revenue.previous || 0) > 0);

  const fmtMoney = (v) => {
    const n = Number(v || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency", currency, maximumFractionDigits: 0,
      }).format(n);
    } catch {
      return `${n.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${currency}`;
    }
  };

  const compareData = [
    { label: t("dashboard.thisPeriod"), value: Math.round(revenue.current || 0) },
    { label: t("dashboard.lastPeriod"), value: Math.round(revenue.previous || 0) },
  ];

  // Products-sold stacked series (quantity per product, summed across packaging/units).
  const productKeys = data?.product_keys || [];
  const seriesProducts = data?.series_products || [];
  const hasProductData = seriesProducts.some((row) => productKeys.some((p) => row[p] > 0));

  // Trend styling: up = brand/positive, down = destructive, flat/new = muted.
  let TrendIcon = Minus;
  let trendTone = "text-muted-foreground bg-secondary";
  let trendText = t("dashboard.newSales");
  if (changePct != null) {
    if (changePct > 0) {
      TrendIcon = TrendingUp;
      trendTone = "text-[hsl(var(--brand))] bg-[hsl(var(--brand)/0.1)]";
      trendText = `+${changePct}%`;
    } else if (changePct < 0) {
      TrendIcon = TrendingDown;
      trendTone = "text-[hsl(var(--destructive))] bg-[hsl(var(--destructive)/0.1)]";
      trendText = `${changePct}%`;
    } else {
      trendText = "0%";
    }
  }

  return (
    <Card className="mt-6" data-testid="sales-trend-widget">
      <CardHeader className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShoppingCart className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("dashboard.salesTrend")}
          </CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">{t("dashboard.salesTrendValueSub")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={product} onValueChange={setProduct}>
            <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="sales-trend-product">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL} data-testid="sales-trend-product-all">{t("dashboard.allProducts")}</SelectItem>
              {products.map((p) => (
                <SelectItem key={p} value={p} data-testid={`sales-trend-product-${p}`}>{td(p)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-9 w-[150px] text-sm" data-testid="sales-trend-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => (
                <SelectItem key={p} value={p} data-testid={`sales-trend-period-${p}`}>{periodLabel[p]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-[240px] animate-pulse rounded-lg bg-secondary" />
        ) : !hasData ? (
          <div className="flex h-[220px] flex-col items-center justify-center text-center" data-testid="sales-trend-empty">
            <ShoppingCart className="h-9 w-9 text-muted-foreground/40" />
            <p className="mt-3 text-sm text-muted-foreground">{t("dashboard.noSales")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 items-center gap-6 md:grid-cols-2">
            {/* Big revenue value + change vs last period */}
            <div data-testid="sales-trend-summary">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t("dashboard.revenueLabel")} · {t("dashboard.thisPeriod")}
              </p>
              <div className="mt-1 flex items-end gap-3">
                <span className="text-3xl font-bold tracking-tight text-foreground lg:text-4xl" data-testid="sales-trend-current-value">
                  {fmtMoney(revenue.current)}
                </span>
                <span
                  className={`mb-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${trendTone}`}
                  data-testid="sales-trend-change"
                >
                  <TrendIcon className="h-3.5 w-3.5" />
                  {trendText}
                </span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {t("dashboard.lastPeriod")}:{" "}
                <span className="font-medium text-foreground" data-testid="sales-trend-previous-value">
                  {fmtMoney(revenue.previous)}
                </span>
              </p>
            </div>

            {/* Mini 2-bar comparison: this period vs last period */}
            <div className="h-[180px]" data-testid="sales-trend-compare-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={compareData} margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
                  <YAxis fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" width={56} tickFormatter={(v) => fmtMoney(v)} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} cursor={BAR_CURSOR} formatter={(v) => [fmtMoney(v), t("dashboard.revenueLabel")]} />
                  <Bar dataKey="value" name={t("dashboard.revenueLabel")} radius={[6, 6, 0, 0]} maxBarSize={72}>
                    {compareData.map((entry, i) => (
                      <Cell key={entry.label} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Products sold over time (stacked by product) */}
        {!loading && hasProductData && (
          <div className="mt-6 border-t border-border pt-5" data-testid="sales-trend-products-section">
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("dashboard.byProduct")}
            </p>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={seriesProducts} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" interval="preserveStartEnd" minTickGap={16} />
                <YAxis fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={BAR_CURSOR} />
                <Legend wrapperStyle={LEGEND_STYLE} />
                {productKeys.map((p, i) => (
                  <Bar
                    key={p}
                    dataKey={p}
                    name={td(p)}
                    stackId="sales"
                    fill={CHART_COLORS[i % CHART_COLORS.length]}
                    radius={i === productKeys.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <Button variant="ghost" size="sm" className="text-[hsl(var(--brand))]" onClick={() => navigate(appPath("/reports"))} data-testid="sales-trend-report-link">
            {t("dashboard.viewDetailedReport")} <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
